/**
 * Participant rolling-window cap helpers shared by Session Type Booking Rules
 * and booking-time validation. Mirrors data-collector-be/services/participantWindowCap.js.
 */

import type {BookingRules} from './bookingRules';

export const WINDOW_LIMIT_HOURS = 'hours';
export const WINDOW_LIMIT_SESSIONS = 'sessions';

export type WindowLimitBasis = typeof WINDOW_LIMIT_HOURS | typeof WINDOW_LIMIT_SESSIONS;

export interface WindowCapFormState {
  enabled: boolean;
  basis: WindowLimitBasis;
  windowDays: string;
  capacity: string;
  maxHoursPerDay: string;
}

export const emptyWindowCapForm = (): WindowCapFormState => ({
  enabled: false,
  basis: WINDOW_LIMIT_HOURS,
  windowDays: '',
  capacity: '',
  maxHoursPerDay: '',
});

/** OD-1260 one-way dependency: a window cap requires multi-booking, not vice versa. */
export const resolveAllowMultipleBookings = (
  windowCapEnabled: boolean,
  allowMultipleBookings: boolean,
): boolean => windowCapEnabled || allowMultipleBookings;

export const windowCapFormFromBookingRules = (rules: BookingRules): WindowCapFormState => ({
  enabled: rules.capParticipantSessionsInWindow,
  basis: rules.windowLimitBasis ?? WINDOW_LIMIT_HOURS,
  windowDays: rules.windowDays != null ? String(rules.windowDays) : '',
  capacity: rules.windowCapacity != null ? String(rules.windowCapacity) : '',
  maxHoursPerDay:
    rules.maxHoursPerDay != null
      ? String(rules.maxHoursPerDay)
      : '',
});

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_HOUR = 60 * 60 * 1000;

const toDate = (value: Date | string): Date =>
  value instanceof Date ? value : new Date(value);

export const overlapMs = (
  aStart: Date | string,
  aEnd: Date | string,
  bStart: Date | string,
  bEnd: Date | string,
): number => {
  const start = Math.max(toDate(aStart).getTime(), toDate(bStart).getTime());
  const end = Math.min(toDate(aEnd).getTime(), toDate(bEnd).getTime());
  return end > start ? end - start : 0;
};

const windowSpanMs = (windowDays: number): number => {
  const spanMs = windowDays * MS_PER_DAY;
  // A window is never shorter than the calendar day the per-day cap needs.
  return Number.isFinite(spanMs) && spanMs > MS_PER_DAY ? spanMs : MS_PER_DAY;
};

const parsePositiveNumber = (raw: string): number | null => {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const parsePositiveInt = (raw: string): number | null => {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 ? n : null;
};

export const validateWindowCapForm = (form: WindowCapFormState): string | undefined => {
  if (!form.enabled) return undefined;
  if (form.basis !== WINDOW_LIMIT_HOURS && form.basis !== WINDOW_LIMIT_SESSIONS) {
    return 'Limit basis is required when the participant time-window cap is enabled';
  }
  if (parsePositiveInt(form.windowDays) == null) {
    return 'Time window must be a whole number of 1 or greater';
  }
  if (form.basis === WINDOW_LIMIT_HOURS && parsePositiveNumber(form.capacity) == null) {
    return 'Max hours per window must be a number greater than 0';
  }
  if (form.basis === WINDOW_LIMIT_SESSIONS && parsePositiveInt(form.capacity) == null) {
    return 'Max sessions per window must be a whole number of 1 or greater';
  }
  if (form.maxHoursPerDay.trim() !== '' && parsePositiveNumber(form.maxHoursPerDay) == null) {
    return 'Max hours per day must be a number greater than 0 when provided';
  }
  return undefined;
};

export const windowCapFormToPayload = (form: WindowCapFormState) => {
  if (!form.enabled) {
    return {
      cap_participant_sessions_in_window: false,
      window_limit_basis: null,
      window_days: null,
      window_capacity: null,
      max_hours_per_day: null,
    };
  }
  return {
    cap_participant_sessions_in_window: true,
    window_limit_basis: form.basis,
    window_days: parsePositiveInt(form.windowDays),
    window_capacity:
      form.basis === WINDOW_LIMIT_HOURS
        ? parsePositiveNumber(form.capacity)
        : parsePositiveInt(form.capacity),
    max_hours_per_day:
      form.maxHoursPerDay.trim() === '' ? null : parsePositiveNumber(form.maxHoursPerDay),
  };
};

export const formatWindowCapPreview = (form: WindowCapFormState): string | null => {
  if (!form.enabled) return null;
  const days = parsePositiveInt(form.windowDays);
  const capacity =
    form.basis === WINDOW_LIMIT_SESSIONS
      ? parsePositiveInt(form.capacity)
      : parsePositiveNumber(form.capacity);
  if (days == null || capacity == null) return null;
  const unit = form.basis === WINDOW_LIMIT_SESSIONS ? 'sessions' : 'hours';
  const verb = form.basis === WINDOW_LIMIT_SESSIONS ? 'book' : 'work';
  const maxHoursPerDay = parsePositiveNumber(form.maxHoursPerDay);
  const dailyText =
    maxHoursPerDay == null
      ? ''
      : ` No more than ${maxHoursPerDay} hours are allowed on any single calendar day.`;
  return `Participants can ${verb} a maximum of ${capacity} ${unit} within any rolling ${days}-day window.${dailyText}`;
};

export interface WindowCapSession {
  start_time?: string | Date;
  end_time?: string | Date;
  status?: string | null;
}

interface AnchoredWindow {
  windowStart: Date;
  windowEnd: Date;
  sessions: WindowCapSession[];
}

const startOfCalendarDay = (value: Date | string): Date => {
  const date = toDate(value);
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
};

const buildCalendarDayWindows = (startTime: Date | string, endTime: Date | string) => {
  const start = toDate(startTime);
  const end = toDate(endTime);
  if (end <= start) return [];

  const windows: Array<{dayStart: Date; dayEnd: Date}> = [];
  let dayStart = startOfCalendarDay(start);
  while (dayStart < end) {
    const dayEnd = new Date(dayStart.getTime() + MS_PER_DAY);
    windows.push({dayStart, dayEnd});
    dayStart = dayEnd;
  }
  return windows;
};

const exceedsDailyHoursCap = ({
  startTime,
  endTime,
  maxHoursPerDay,
  existingSessions,
}: {
  startTime: Date | string;
  endTime: Date | string;
  maxHoursPerDay: number | null;
  existingSessions: WindowCapSession[];
}): boolean => {
  if (maxHoursPerDay == null) return false;
  return buildCalendarDayWindows(startTime, endTime).some(({dayStart, dayEnd}) => {
    const existingMs = existingSessions.reduce((sum, session) => {
      if (!session.start_time) return sum;
      return sum + overlapMs(
        session.start_time,
        session.end_time || session.start_time,
        dayStart,
        dayEnd,
      );
    }, 0);
    const requestedMs = overlapMs(startTime, endTime, dayStart, dayEnd);
    return existingMs + requestedMs > maxHoursPerDay * MS_PER_HOUR;
  });
};

const buildAnchoredWindows = (
  existingSessions: WindowCapSession[],
  windowDays: number,
): AnchoredWindow[] => {
  const spanMs = windowSpanMs(windowDays);
  const sorted = existingSessions
    .filter((session) => session.start_time)
    .slice()
    .sort(
      (left, right) =>
        toDate(left.start_time!).getTime() - toDate(right.start_time!).getTime(),
    );
  const windows: AnchoredWindow[] = [];

  sorted.forEach((session) => {
    const latest = windows[windows.length - 1];
    const sessionStart = toDate(session.start_time!);
    if (!latest || sessionStart >= latest.windowEnd) {
      const windowStart = startOfCalendarDay(sessionStart);
      windows.push({
        windowStart,
        windowEnd: new Date(windowStart.getTime() + spanMs),
        sessions: [session],
      });
      return;
    }
    latest.sessions.push(session);
  });
  return windows;
};

const evaluateAnchoredSessionCapacity = ({
  capacity,
  windowDays,
  startTime,
  existingSessions,
}: {
  capacity: number;
  windowDays: number;
  startTime: Date | string;
  existingSessions: WindowCapSession[];
}): boolean => {
  const requestedSession = {start_time: startTime, requested: true};
  const requestedWindow = buildAnchoredWindows(
    [...existingSessions, requestedSession],
    windowDays,
  ).find((window) => window.sessions.includes(requestedSession));
  return (requestedWindow?.sessions.length || 0) > capacity;
};

const rollingHoursAtReference = (
  existingSessions: WindowCapSession[],
  windowDays: number,
  referenceTime: Date | string,
): number => {
  const reference = toDate(referenceTime);
  const referenceEnd = new Date(reference.getTime() + 1);
  const candidates = buildRollingWindowCandidates({
    startTime: reference,
    endTime: referenceEnd,
    windowDays,
    existingSessions,
  });
  return candidates.reduce((highestUsage, {windowStart, windowEnd}) => {
    const usage = existingSessions.reduce((sum, session) => {
      if (!session.start_time) return sum;
      return (
        sum +
        overlapMs(
          session.start_time,
          session.end_time || session.start_time,
          windowStart,
          windowEnd,
        )
      );
    }, 0);
    return Math.max(highestUsage, usage / MS_PER_HOUR);
  }, 0);
};

export const hasRemainingWindowCapCapacity = ({
  enabled,
  basis,
  windowDays,
  capacity,
  maxHoursPerDay,
  existingSessions,
  referenceTime = new Date(),
}: {
  enabled: boolean;
  basis: WindowLimitBasis | null;
  windowDays: number | null;
  capacity: number | null;
  maxHoursPerDay: number | null;
  existingSessions: WindowCapSession[];
  referenceTime?: Date | string;
}): boolean => {
  if (!enabled || !basis || windowDays == null || capacity == null) return true;
  const reference = toDate(referenceTime);
  const currentWindow =
    basis === WINDOW_LIMIT_SESSIONS
      ? buildAnchoredWindows(existingSessions, windowDays).find(
          ({windowEnd}) => windowEnd > reference,
        )
      : {
          windowStart: startOfCalendarDay(reference),
          windowEnd: new Date(
            startOfCalendarDay(reference).getTime() + windowSpanMs(windowDays),
          ),
          sessions: existingSessions,
        };
  if (!currentWindow) return true;
  const rollingUsage =
    basis === WINDOW_LIMIT_SESSIONS
      ? currentWindow.sessions.length
      : rollingHoursAtReference(existingSessions, windowDays, reference);
  if (rollingUsage >= capacity) return false;
  if (maxHoursPerDay == null) return true;

  const firstCandidateDay = new Date(
    Math.max(
      startOfCalendarDay(referenceTime).getTime(),
      currentWindow.windowStart.getTime(),
    ),
  );
  return buildCalendarDayWindows(firstCandidateDay, currentWindow.windowEnd).some(
    ({dayStart, dayEnd}) => {
      const usedMs = currentWindow.sessions.reduce(
        (sum, session) =>
          sum +
          overlapMs(
            session.start_time!,
            session.end_time || session.start_time!,
            dayStart,
            dayEnd,
          ),
        0,
      );
      return usedMs < maxHoursPerDay * MS_PER_HOUR;
    },
  );
};

/**
 * The N-day windows worth testing for a booking. A window's load can only peak
 * when its start sits on a session start or its end on a session end, so those
 * are the only anchors that matter, and of those only the windows the requested
 * session itself falls in — a window it is not part of cannot be pushed over
 * the cap by booking it.
 */
const buildRollingWindowCandidates = ({
  startTime,
  endTime,
  windowDays,
  existingSessions,
}: {
  startTime: Date | string;
  endTime: Date | string;
  windowDays: number;
  existingSessions: WindowCapSession[];
}): Array<{windowStart: Date; windowEnd: Date}> => {
  const spanMs = windowSpanMs(windowDays);
  const requestedStart = toDate(startTime).getTime();
  const requestedEnd = toDate(endTime).getTime();

  const anchors = new Set<number>();
  const addAnchors = (sessionStart: Date | string, sessionEnd: Date | string) => {
    anchors.add(toDate(sessionStart).getTime());
    anchors.add(toDate(sessionEnd).getTime() - spanMs);
  };
  addAnchors(startTime, endTime);
  existingSessions.forEach((session) => {
    if (!session.start_time) return;
    addAnchors(session.start_time, session.end_time || session.start_time);
  });

  return [...anchors]
    .filter((anchor) => anchor < requestedEnd && anchor + spanMs > requestedStart)
    .map((anchor) => ({
      windowStart: new Date(anchor),
      windowEnd: new Date(anchor + spanMs),
    }));
};

const windowExceedsHoursCapacity = ({
  capacity,
  windowStart,
  windowEnd,
  startTime,
  endTime,
  existingSessions,
}: {
  capacity: number;
  windowStart: Date;
  windowEnd: Date;
  startTime: Date | string;
  endTime: Date | string;
  existingSessions: WindowCapSession[];
}): boolean => {
  const overlapping = existingSessions.filter((session) => {
    if (!session.start_time) return false;
    return overlapMs(
      session.start_time,
      session.end_time || session.start_time,
      windowStart,
      windowEnd,
    ) > 0;
  });
  const existingMs = overlapping.reduce(
    (sum, session) =>
      sum +
      overlapMs(
        session.start_time!,
        session.end_time || session.start_time!,
        windowStart,
        windowEnd,
      ),
    0,
  );
  return (
    existingMs + overlapMs(startTime, endTime, windowStart, windowEnd) >
    capacity * MS_PER_HOUR
  );
};

export const evaluateWindowCapForBooking = ({
  enabled,
  basis,
  windowDays,
  capacity,
  maxHoursPerDay,
  startTime,
  endTime,
  existingSessions,
}: {
  enabled: boolean;
  basis: WindowLimitBasis | null;
  windowDays: number | null;
  capacity: number | null;
  maxHoursPerDay: number | null;
  startTime: Date | string;
  endTime: Date | string;
  existingSessions: WindowCapSession[];
}): 'dailyHours' | 'hours' | 'sessions' | null => {
  if (!enabled || !basis || windowDays == null || capacity == null) return null;
  if (exceedsDailyHoursCap({startTime, endTime, maxHoursPerDay, existingSessions})) {
    return 'dailyHours';
  }
  if (basis === WINDOW_LIMIT_SESSIONS) {
    return evaluateAnchoredSessionCapacity({
      capacity,
      windowDays,
      startTime,
      existingSessions,
    })
      ? 'sessions'
      : null;
  }

  const exceeded = buildRollingWindowCandidates({
    startTime,
    endTime,
    windowDays,
    existingSessions,
  }).some(({windowStart, windowEnd}) =>
    windowExceedsHoursCapacity({
      capacity,
      windowStart,
      windowEnd,
      startTime,
      endTime,
      existingSessions,
    }),
  );
  if (!exceeded) return null;
  return 'hours';
};
